/**
 * ControlBudgetModelsEditor — admin tool to view and edit Control Budget Models (Airtable
 * tblEdUZrWv4mbudzU), the small $/SF cost-template catalog a Project Concept assigns via
 * ControlBudgetSection.jsx (which only ASSIGNS a model — this is the first place the model's own
 * numbers can be edited without opening Airtable directly). Feeds present/BudgetExplorer.jsx and
 * every room's `Budget Room Cost Low/High` formula (see docs/PROGRAM_MODEL.md).
 *
 * Opened via `open-control-budget-models-editor`; mounted once in OutlineView; open to anyone who can edit templates (admin or checklist-templates), 2026-09-24.
 * Left list of models (typically one per active project/vintage — ~5 today), right form for the
 * selected model grouped into Category $/SF, Scope Factors, Gross Area Factor, and Pool/Spa lump
 * sums. Edits persist on blur directly to Airtable via services/design.js.
 *
 * Scope Factors each show their SCOPE_TYPE_DEFINITIONS text (outline/constants.js) below the
 * input — the same definition a designer sees when picking that tier on a room, so an admin
 * tuning a factor's number sees what it's actually supposed to mean (including the "remodel +
 * addition in the same room" guidance on Moderate/Heavy and the "when altering costs more than
 * building new" guidance on Major — see docs/PROGRAM_MODEL.md).
 */

import React, { useEffect, useState } from 'react'
import { hasModule, isSeatLoaded } from '../../services/tenancy.js'
import { getControlBudgetModelsFull, updateControlBudgetModel, createControlBudgetModel, deleteControlBudgetModel } from '../../services/design.js'
import { alertDialog, confirmDialog } from '../Dialog.jsx'
import { SCOPE_TYPE_DEFINITIONS } from '../../outline/constants.js'

// Maps each Control Budget Model factor field to the Scope Type name its definition is keyed
// under in SCOPE_TYPE_DEFINITIONS — same source of truth the room-level Scope Type pickers use
// (ProgramModelModal.jsx, ConceptRoomsSection.jsx), so an admin tuning these numbers sees exactly
// what a designer sees when picking the tier, including the remodel+addition guidance.
const SCOPE_TYPE_BY_FIELD = {
  'Cosmetic Refresh Factor': 'Cosmetic Refresh',
  'Light Alteration Factor': 'Light Alteration',
  'Moderate Alteration Factor': 'Moderate Alteration',
  'Heavy Alteration Factor': 'Heavy Alteration',
  'Major Alteration Factor': 'Major Alteration',
  'Existing to Remain Factor': 'Existing to Remain',
}

// Link fields never carried over when duplicating a model as a starting point — a copy starts
// unassigned to any Project/Concept regardless of what its source was assigned to.
const LINK_FIELDS = ['Projects', 'Projects copy']

const CATEGORIES = ['Occupiable', 'Garage', 'Storage Utility', 'Elevated Deck', 'Covered Patio', 'Site Improvement']

// Scope factor fields, light-to-heavy (Existing to Remain kept last to match SCOPE_TYPE_OPTIONS
// everywhere else in the app) — 'New Construction' has no field; the Budget Room Cost formula
// hardcodes it to 1.0, shown read-only for context. 'Cosmetic Refresh Factor' exists on this
// table now, but Rooms and Spaces can't select it yet (no Scope Type choice, no lookup chain,
// formula not updated) — see docs/PROGRAM_MODEL.md "Scope Type — cosmetic-refresh gap".
const SCOPE_FACTORS = ['Cosmetic Refresh Factor', 'Light Alteration Factor', 'Moderate Alteration Factor', 'Heavy Alteration Factor', 'Major Alteration Factor', 'Existing to Remain Factor']

// Exact Airtable field names — note the double space in 'Pool Only Cost  - Medium' (verified via
// describe_table; not a typo to "fix", it's the live field name).
const POOL_LUMP_SUMS = [
  { field: 'Spa Only Cost', label: 'Spa Only' },
  { field: 'Pool Only Cost - Small', label: 'Pool Only — Small' },
  { field: 'Pool Only Cost  - Medium', label: 'Pool Only — Medium' },
  { field: 'Pool Only Cost - Large', label: 'Pool Only — Large' },
  { field: 'Pool Spa Cost - Small', label: 'Pool + Spa — Small' },
  { field: 'Pool Spa Cost - Medium', label: 'Pool + Spa — Medium' },
  { field: 'Pool Spa Cost - Large', label: 'Pool + Spa — Large' },
]

function n(v) { return v == null ? '' : String(v) }

export default function ControlBudgetModelsEditor() {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [copyFromId, setCopyFromId] = useState('')

  useEffect(() => {
    function onOpen() { setOpen(true); load() }
    window.addEventListener('open-control-budget-models-editor', onOpen)
    return () => window.removeEventListener('open-control-budget-models-editor', onOpen)
  }, [])

  async function load() {
    setLoading(true); setError(null)
    try {
      const list = await getControlBudgetModelsFull()
      setRows(list)
      setSelectedId(prev => (prev && list.some(r => r.id === prev)) ? prev : (list[0]?.id || null))
    } catch (e) { setError(e.message || 'Failed to load') }
    finally { setLoading(false) }
  }

  function close() { setOpen(false); setRows([]); setSelectedId(null); setError(null) }

  const selected = rows.find(r => r.id === selectedId) || null

  function setLocal(field, value) {
    setRows(prev => prev.map(r => r.id === selectedId ? { ...r, fields: { ...r.fields, [field]: value } } : r))
  }

  async function persist(field, value) {
    setBusy(true)
    try { await updateControlBudgetModel(selectedId, { [field]: value }) }
    catch (e) { await alertDialog({ title: 'Save failed', message: e.message }); load() }
    finally { setBusy(false) }
  }

  function openCreate() { setCreating(true); setNewName(''); setCopyFromId('') }
  function cancelCreate() { setCreating(false) }

  async function handleCreate() {
    const name = newName.trim()
    if (!name) { await alertDialog({ title: 'Name required', message: 'Give the new model a name.' }); return }
    const source = copyFromId ? rows.find(r => r.id === copyFromId) : null
    const fields = { Name: name }
    if (source) {
      for (const key of Object.keys(source.fields)) {
        if (key === 'Name' || LINK_FIELDS.includes(key)) continue
        fields[key] = source.fields[key]
      }
    }
    setBusy(true)
    try {
      const rec = await createControlBudgetModel(fields)
      setRows(prev => [...prev, rec])
      setSelectedId(rec.id)
      setCreating(false)
    } catch (e) { await alertDialog({ title: 'Create failed', message: e.message }) }
    finally { setBusy(false) }
  }

  async function handleDelete() {
    if (!selected) return
    const usedProjects = (selected.fields['Projects'] || []).length
    const usedConcepts = (selected.fields['Projects copy'] || []).length
    const usage = usedProjects || usedConcepts
      ? ' Used by ' + usedProjects + ' project(s) and ' + usedConcepts + ' concept(s) — they will lose this assignment.'
      : ' Not currently used by any project or concept.'
    if (!(await confirmDialog({ title: 'Delete model?', message: 'Delete “' + (selected.fields['Name'] || '(untitled)') + '”?' + usage, confirmLabel: 'Delete model', danger: true }))) return
    setBusy(true)
    try {
      await deleteControlBudgetModel(selected.id)
      setRows(prev => prev.filter(r => r.id !== selected.id))
      setSelectedId(null)
    } catch (e) { await alertDialog({ title: 'Delete failed', message: e.message }) }
    finally { setBusy(false) }
  }

  // desc renders as its own full-width line below the row — used for the Scope Type
  // definitions, which run several sentences (the remodel+addition guidance on Moderate/Heavy,
  // the "when does altering cost more than new" guidance on Major) and would cramp badly inlined
  // next to the label the way the short `hint` param already does for e.g. Gross Area Factor.
  function numField(label, field, hint, desc) {
    const raw = selected?.fields?.[field]
    return (
      <div key={field}>
        <div style={S.fieldRow}>
          <span style={S.fieldLabel}>{label}{hint && <span style={S.fieldHint}> — {hint}</span>}</span>
          <input type="number" style={S.inp} value={n(raw)}
            onChange={e => setLocal(field, e.target.value === '' ? null : Number(e.target.value))}
            onBlur={e => persist(field, e.target.value === '' ? null : Number(e.target.value))} />
        </div>
        {desc && <div style={S.desc}>{desc}</div>}
      </div>
    )
  }

  if (!open) return null
  if (isSeatLoaded() && !hasModule('admin') && !hasModule('checklist-templates')) {   // anyone who can edit templates (Matt, 2026-09-24)
    return (
      <>
        <div style={S.backdrop} onClick={close} />
        <div style={S.modal}><div style={S.restricted}>Only people who can edit templates can open this.</div><div style={S.btns}><button style={S.ghost} onClick={close}>Close</button></div></div>
      </>
    )
  }

  return (
    <>
      <div style={S.backdrop} onClick={close} />
      <div style={S.modal} role="dialog" aria-label="Control Budget Models">
        <div style={S.headRow}>
          <span style={S.kicker}>Control Budget Models</span>
          <button style={S.x} onClick={close} aria-label="Close">×</button>
        </div>
        <div style={S.sub}>The $/SF cost-template catalog a Concept assigns to power its Control Budget and the Program Model's room-level estimates. Edits save immediately.</div>
        {error && <div style={S.err}>{error}</div>}

        {loading ? <div style={S.muted}>Loading…</div> : (
          <div style={S.split}>
            <div style={S.list}>
              {rows.map(r => (
                <button key={r.id} onClick={() => setSelectedId(r.id)}
                  style={{ ...S.listItem, ...(r.id === selectedId ? S.listItemActive : {}) }}>
                  {r.fields['Name'] || '(untitled)'}
                </button>
              ))}
              {creating ? (
                <div style={S.createBox}>
                  <input style={S.inp} placeholder="New model name" autoFocus value={newName} onChange={e => setNewName(e.target.value)} />
                  <select style={{ ...S.inp, marginTop: 6 }} value={copyFromId} onChange={e => setCopyFromId(e.target.value)}>
                    <option value="">Start blank</option>
                    {rows.map(r => <option key={r.id} value={r.id}>Copy from “{r.fields['Name'] || '(untitled)'}”</option>)}
                  </select>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button style={S.ghost} onClick={cancelCreate}>Cancel</button>
                    <button style={S.primary} disabled={busy} onClick={handleCreate}>Create</button>
                  </div>
                </div>
              ) : (
                <button style={S.addBtn} onClick={openCreate}>+ New model</button>
              )}
            </div>

            <div style={{ ...S.form, opacity: busy ? 0.6 : 1 }}>
              {!selected ? <div style={S.muted}>{rows.length ? 'Select a model.' : 'No models yet — create one.'}</div> : <>
                <div style={S.fieldRow}>
                  <span style={S.fieldLabel}>Name</span>
                  <input style={S.inp} value={selected.fields['Name'] || ''}
                    onChange={e => setLocal('Name', e.target.value)}
                    onBlur={e => persist('Name', e.target.value.trim())} />
                </div>
                <div style={{ ...S.fieldRow, alignItems: 'baseline' }}>
                  <span style={{ color: 'var(--aj-text-muted)', fontSize: 11 }}>
                    Used by {(selected.fields['Projects'] || []).length} project(s), {(selected.fields['Projects copy'] || []).length} concept(s) — assignment happens on the Concept, not here.
                  </span>
                  <button style={S.deleteBtn} onClick={handleDelete}>Delete model</button>
                </div>

                <div style={S.sectionHead}>Category $/SF</div>
                <div style={S.grid2}>
                  <span style={S.gridHead}>Category</span><span style={S.gridHead}>Low</span><span style={S.gridHead}>High</span>
                  {CATEGORIES.map(cat => (
                    <React.Fragment key={cat}>
                      <span style={S.gridLabel}>{cat}</span>
                      <input type="number" style={S.inp} value={n(selected.fields[cat + ' Cost Per SF Low'])}
                        onChange={e => setLocal(cat + ' Cost Per SF Low', e.target.value === '' ? null : Number(e.target.value))}
                        onBlur={e => persist(cat + ' Cost Per SF Low', e.target.value === '' ? null : Number(e.target.value))} />
                      <input type="number" style={S.inp} value={n(selected.fields[cat + ' Cost Per SF High'])}
                        onChange={e => setLocal(cat + ' Cost Per SF High', e.target.value === '' ? null : Number(e.target.value))}
                        onBlur={e => persist(cat + ' Cost Per SF High', e.target.value === '' ? null : Number(e.target.value))} />
                    </React.Fragment>
                  ))}
                  <span style={{ ...S.gridLabel, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>Pool Spa (unused — lump sums below drive Pool/Spa pricing instead)</span>
                  <input type="number" style={{ ...S.inp, opacity: 0.5 }} value={n(selected.fields['Pool Spa Cost Per SF Low'])}
                    onChange={e => setLocal('Pool Spa Cost Per SF Low', e.target.value === '' ? null : Number(e.target.value))}
                    onBlur={e => persist('Pool Spa Cost Per SF Low', e.target.value === '' ? null : Number(e.target.value))} />
                  <input type="number" style={{ ...S.inp, opacity: 0.5 }} value={n(selected.fields['Pool Spa Cost Per SF High'])}
                    onChange={e => setLocal('Pool Spa Cost Per SF High', e.target.value === '' ? null : Number(e.target.value))}
                    onBlur={e => persist('Pool Spa Cost Per SF High', e.target.value === '' ? null : Number(e.target.value))} />
                </div>

                <div style={S.sectionHead}>Scope Factors</div>
                <div style={S.fieldRow}>
                  <span style={S.fieldLabel}>New Construction</span>
                  <span style={{ ...S.inp, background: 'transparent', border: 'none', color: 'var(--aj-text-muted)' }}>1.00× (fixed)</span>
                </div>
                <div style={S.desc}>{SCOPE_TYPE_DEFINITIONS['New Construction']}</div>
                {SCOPE_FACTORS.map(f => numField(f.replace(' Factor', ''), f, null, SCOPE_TYPE_DEFINITIONS[SCOPE_TYPE_BY_FIELD[f]]))}

                <div style={S.sectionHead}>Gross Area Factor</div>
                {numField('Gross Area Factor', 'Gross Area Factor', 'Net → Gross multiplier')}

                <div style={S.sectionHead}>Pool / Spa (lump sum by size)</div>
                {POOL_LUMP_SUMS.map(p => numField(p.label, p.field))}
              </>}
            </div>
          </div>
        )}

        <div style={S.btns}><button style={S.ghost} onClick={close}>Done</button></div>
      </div>
    </>
  )
}

const MONO = '"DM Mono",monospace'
const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1400 },
  modal: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(760px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 14, zIndex: 1401, padding: '16px 18px 14px', boxShadow: '0 18px 60px rgba(0,0,0,0.28)', fontFamily: '"DM Sans",sans-serif', color: 'var(--aj-text)' },
  headRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  kicker: { fontFamily: 'Montserrat,sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  x: { fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  sub: { fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4, lineHeight: 1.5 },
  err: { marginTop: 8, fontSize: 12.5, color: '#c0392b' },
  muted: { fontSize: 12.5, color: 'var(--aj-text-muted)', padding: 12 },
  split: { display: 'flex', gap: 14, marginTop: 12, flex: 1, overflow: 'hidden' },
  list: { width: 200, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', borderRight: '0.5px solid var(--aj-border)', paddingRight: 10 },
  listItem: { textAlign: 'left', fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer', lineHeight: 1.35 },
  listItemActive: { background: 'var(--aj-bg-subtle)', fontWeight: 600 },
  addBtn: { marginTop: 6, textAlign: 'left', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#1a6fa8', background: 'none', border: '0.5px solid rgba(26,111,168,0.4)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' },
  createBox: { marginTop: 6, padding: 8, borderRadius: 6, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-subtle)' },
  primary: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' },
  deleteBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#b8272c', background: 'none', border: '0.5px solid rgba(184,39,44,0.35)', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', whiteSpace: 'nowrap' },
  form: { flex: 1, overflowY: 'auto', paddingRight: 4 },
  sectionHead: { fontFamily: 'Montserrat,sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginTop: 14, marginBottom: 4, borderBottom: '0.5px solid var(--aj-border)', paddingBottom: 3 },
  fieldRow: { display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 10, alignItems: 'center', padding: '3px 0' },
  fieldLabel: { fontSize: 12, color: 'var(--aj-text-mid)' },
  fieldHint: { color: 'var(--aj-text-muted)', fontSize: 10.5 },
  desc: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', lineHeight: 1.4, padding: '1px 0 5px' },
  grid2: { display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 6, alignItems: 'center' },
  gridHead: { fontSize: 9.5, color: 'var(--aj-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Montserrat,sans-serif', paddingBottom: 2 },
  gridLabel: { fontSize: 12, color: 'var(--aj-text-mid)', padding: '3px 0' },
  inp: { fontFamily: MONO, fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', width: '100%', boxSizing: 'border-box' },
  btns: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '0.5px solid var(--aj-border)' },
  ghost: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },
  restricted: { fontSize: 13, color: 'var(--aj-text-muted)', padding: 20, textAlign: 'center' },
}
